import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Button } from '@blade/ui-kit'
import { type WalletModel } from '@root/entities/wallet'
import { reatomEditWallet } from '@root/features/wallet'
import { walletsManagerModelAtom } from '@root/pages/wallet-manager/atoms'
import { ChevronRightExactIcon, DatabaseExportIcon } from '@root/shared/ui-kit'

import { WalletDetailsSection } from '../wallet-details-section'

import { EditWalletColor } from './edit-wallet-color'
import { EditWalletDelete } from './edit-wallet-delete'
import { EditWalletName } from './edit-wallet-name'
import { EditWalletPanelItem } from './edit-wallet-panel-item'
import { EditWalletRefresh } from './edit-wallet-refresh'

type Props = {
  wallet: WalletModel
}

export type EditWalletValues = {
  name: string
  color: string
}

export const EditWallet: React.FC<Props> = ({ wallet }) => {
  const { t } = useTranslation()
  const [model] = useAtom(walletsManagerModelAtom)
  const [_, setScreen] = useAtom(model.walletDetailsScreen)
  const clearSelectedWallet = useAction(model.clearSelectedWallet)

  const editModel = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomEditWallet({ wallet }),
    [wallet],
  )
  const [state] = useAtom(editModel)
  const updateWallet = useAction(editModel.execute)
  const [walletColor] = useAtom(wallet.color)
  const [walletName] = useAtom(wallet.name)

  const { handleSubmit, control, formState, reset, watch } =
    useForm<EditWalletValues>({
      defaultValues: {
        name: walletName,
        color: walletColor,
      },
      mode: 'all',
    })
  const handleSubmitInternal = handleSubmit((values) => updateWallet(values))
  const handleReset = () => {
    reset({
      name: walletName,
      color: walletColor,
    })
  }

  useEffect(() => {
    reset({
      name: walletName,
      color: walletColor,
    })
  }, [reset, walletColor, walletName])

  return (
    <Root title={t('wallet-manager.edit.title')} onClose={clearSelectedWallet}>
      <Form onSubmit={handleSubmitInternal}>
        <Options>
          <EditWalletName
            wallet={wallet}
            walletName={watch('name')}
            control={control}
          />
          <EditWalletColor
            wallet={wallet}
            walletColor={watch('color')}
            control={control}
          />
          <EditWalletPanelItem
            icon={<DatabaseExportIcon size={16} />}
            endContent={<ChevronRightExactIcon size={12} />}
            title={t('wallet.export_wallet')}
            onClick={() => setScreen('export')}
          />
          <EditWalletRefresh wallet={wallet} />
          <EditWalletDelete wallet={wallet} />
        </Options>
        <Actions>
          <Button
            disabled={!formState.isDirty}
            variant="secondary"
            color="gray"
            size="h36"
            radius="rounded"
            onClick={handleReset}
          >
            {t('common.cancel')}
          </Button>
          <Button
            busy={state.loading}
            disabled={!formState.isDirty || !formState.isValid}
            variant="solid"
            color="primary"
            size="h36"
            radius="rounded"
            type="submit"
          >
            {t('common.save')}
          </Button>
        </Actions>
      </Form>
    </Root>
  )
}

const Root = styled(WalletDetailsSection)`
  background: var(--background-1);
  height: 100%;
  color: var(--text-11);
  font-size: 12px;

  & ${WalletDetailsSection.Content} {
    grid-template-rows: minmax(0, 1fr) auto;

    padding-inline: 12px;
    padding-block: 10px;
  }
`

const Form = styled.form`
  height: 100%;
  display: grid;
  gap: 8px;
  align-items: start;
  grid-template-rows: minmax(0, 1fr) auto;
`

const Options = styled.div`
  display: grid;
  gap: 8px;
`

const Actions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
`
